import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../lib/firebase';
import { useUser } from '../auth/AuthProvider';
import { useEffectiveUid } from '../auth/TargetUidContext';

export type CalendarProvider = 'google' | 'microsoft';

export interface CalendarPrefs {
  primaryProvider: CalendarProvider;
  readSecondary: boolean;
}

export function useCalendarPrefs() {
  const { user } = useUser();
  const targetUid = useEffectiveUid();
  return useQuery<CalendarPrefs>({
    queryKey: ['calendar-prefs', targetUid] as const,
    enabled: !!user && !!targetUid,
    staleTime: 30_000,
    queryFn: async () => {
      const fn = httpsCallable<unknown, { prefs: CalendarPrefs }>(
        functions,
        'getCalendarPrefs',
      );
      const r = await fn({});
      return r.data.prefs;
    },
  });
}

export function useSetCalendarPrefs() {
  const qc = useQueryClient();
  const targetUid = useEffectiveUid();
  return useMutation({
    mutationFn: async (input: Partial<CalendarPrefs>) => {
      const fn = httpsCallable<Partial<CalendarPrefs>, { ok: true }>(
        functions,
        'setCalendarPrefs',
      );
      await fn(input);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['calendar-prefs', targetUid] }),
  });
}
